import "./Services.scss";

import { gsap } from "gsap";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Section from "../../../components/Section/Section";
import SectionHeader from "../../../components/SectionHeader/SectionHeader";
import ShowMoreButton from "../../../components/ShowMoreButton/ShowMoreButton";
import ServiceCard from "./ServiceCard/ServiceCard";
import {
  AiIcon,
  ApiIcon,
  ConsultingIcon,
  DevOpsIcon,
  GameEngineIcon,
  GameIcon,
  MinecraftIcon,
  MobileAppIcon,
  ProtocolIcon,
  SecurityIcon,
  SmartContractIcon,
  WalletIcon,
  WebAppIcon,
} from "../../../assets/svg/svgIcons";

const SERVICE_ICONS = [
  <SmartContractIcon />,
  <WalletIcon />,
  <ProtocolIcon />,
  <ConsultingIcon />,
  <WebAppIcon />,
  <MobileAppIcon />,
  <ApiIcon />,
  <DevOpsIcon />,
  <SecurityIcon />,
  <AiIcon />,
  <MinecraftIcon />,
  <GameIcon />,
  <GameEngineIcon />,
];

const VISIBLE_COUNT = 6;
// Display order follows the current commercial positioning:
// Apps -> Web -> Blockchain -> Games -> supporting capabilities.
const DISPLAY_ORDER = [5, 4, 6, 0, 1, 2, 10, 11, 12, 7, 8, 9, 3];

const Services = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const hiddenServicesRef = useRef<HTMLDivElement>(null);

  const services = t("services.list", { returnObjects: true }) as Array<{
    title: string;
    description: string;
    tags: string[];
  }>;
  const orderedServices = DISPLAY_ORDER.map((sourceIndex) => ({
    ...services[sourceIndex],
    icon: SERVICE_ICONS[sourceIndex],
  }));

  const visibleServices = orderedServices.slice(0, VISIBLE_COUNT);
  const hiddenServices = orderedServices.slice(VISIBLE_COUNT);

  useEffect(() => {
    if (!isOpen || !hiddenServicesRef.current) return;

    const cards = hiddenServicesRef.current.querySelectorAll(".serviceCard");
    if (!cards.length) return;

    gsap.fromTo(
      cards,
      { autoAlpha: 0, y: 8, scale: 0.996 },
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 0.2,
        stagger: 0.02,
        ease: "none",
      },
    );
  }, [isOpen]);

  return (
    <Section id="services" className="services">
      <SectionHeader
        tag={t("services.tag")}
        title={t("services.title")}
        subtitle={t("services.subtitle")}
      />
      <div className="servicesGrid">
        {visibleServices.map((service, index) => (
          <ServiceCard
            key={index}
            number={String(index + 1).padStart(2, "0")}
            icon={service.icon}
            title={service.title}
            description={service.description}
            tags={service.tags}
          />
        ))}
      </div>

      <div
        ref={hiddenServicesRef}
        className={`hiddenServices ${isOpen ? "open" : ""}`}
      >
        {hiddenServices.map((service, index) => (
          <ServiceCard
            key={VISIBLE_COUNT + index}
            number={String(VISIBLE_COUNT + index + 1).padStart(2, "0")}
            icon={service.icon}
            title={service.title}
            description={service.description}
            tags={service.tags}
          />
        ))}
      </div>

      <ShowMoreButton
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        moreLabel={t("services.view_more")}
        lessLabel={t("services.view_less")}
      />
    </Section>
  );
};

export default Services;
